import { EventTracking } from "../../../utilities/Tracking/EventTracking";
import { CkEditorHelper, CkEditorSelection } from "./ckeditor/CkEditorHelper";
import { Editor } from "./ckeditor/Editor";
import { DesktopFormatBubbleSettings } from "./DesktopFormatBubble";
import { DomSlide, DomSlideStyle, EditorSlide, SlideStyle } from "./EditorSlide";
import { MobileFormatBubbleSettings } from "./MobileFormatBubble";
import { SlideAction } from "./SlideAction";
import { v4 as generateGuid } from "uuid";
import visit from "unist-util-visit";
import { PreviewSlide, SlideLine, SlideLineChar } from "./PreviewSlide";

const alignmentOptions = ["center", "left", "right", "justify"] as const;
type ValidAlignmentOption = typeof alignmentOptions[number];

function isValidAlignmentString(maybeCorrectString?: string): maybeCorrectString is ValidAlignmentOption {
	if (maybeCorrectString === undefined) return false;
	return alignmentOptions.includes(maybeCorrectString as ValidAlignmentOption);
}

const ELEMENT_NODE_TYPE = 1;
const blockTags = ["h1", "h2", "p", "ul", "ol"];
const styleTags = ["u", "i", "strong", "li", "blockquote", "mark", "span"];

const isElementOfType = (element: Element, elementType: string | string[]) => {
	if (element != null) {
		if (Array.isArray(elementType)) {
			return elementType.includes(element.tagName.toLowerCase());
		}
		return element.tagName.toLowerCase() === elementType.toLowerCase();
	}
	return false;
};
const isBlockElement = (element: Element) => isElementOfType(element, blockTags);
const isStyleElement = (element: Element) => isElementOfType(element, styleTags);
const isSlideElement = (element: Element) => isElementOfType(element, "slide");
const isLineBreakElement = (element: Element) => isElementOfType(element, "br");
const isListElement = (element: Element) => isElementOfType(element, "li");

const nextPotentialSlideSiblingElement = (element: Element): Element | undefined => {
	let currentElement = element;
	let nextSiblingNode = element.nextSibling;
	while (nextSiblingNode != null && nextSiblingNode.nodeType === ELEMENT_NODE_TYPE) {
		let nextSiblingElement = currentElement.nextElementSibling!;

		currentElement = nextSiblingElement;

		if (!isLineBreakElement(nextSiblingElement)) {
			return nextSiblingElement;
		}

		nextSiblingNode = nextSiblingNode.nextSibling;
	}

	//need to check parent in case of double nested slide
	currentElement = element.parentElement as Element;
	if (currentElement != null && !isBlockElement(currentElement)) {
		return nextPotentialSlideSiblingElement(currentElement) as Element;
	}

	return undefined;
};

const isLastSlide = (element: Element) => {
	const parent = element.parentElement!;
	const nextSiblingNode = element.nextSibling;

	if (!nextSiblingNode) {
		// Last slides in a block tag are the final slide
		if (isBlockElement(parent)) {
			return true;
		}

		const potentialSlideElement = nextPotentialSlideSiblingElement(parent);
		if (!potentialSlideElement) {
			return true;
		}

		// this element is at the end of a style or in a list
		if (isSlideElement(potentialSlideElement) || firstChildIsASlide(potentialSlideElement)) {
			return false;
		}

		// If the first child of the next parent sibling is not a slide, this is the last slide
		return !firstChildIsASlide(potentialSlideElement);
	}

	const potentialSlideElement = nextPotentialSlideSiblingElement(element);
	if (!potentialSlideElement) {
		return true;
	}

	// The next element is another slide, so this is not the last slide
	if (isSlideElement(potentialSlideElement)) {
		return false;
	}

	// if the next element is a style element without a slide being the first child, then we're done
	return !firstChildIsASlide(potentialSlideElement);
};

const firstChildIsASlide = (element: Element) => {
	const firstChildNode = element.firstChild;
	if (firstChildNode === null || firstChildNode.nodeType !== ELEMENT_NODE_TYPE) {
		return false;
	}

	let firstChildElement = element.firstElementChild!;
	while (isStyleElement(firstChildElement)) {
		let tempNode = firstChildElement.firstChild;
		if (!tempNode || tempNode.nodeType !== ELEMENT_NODE_TYPE) {
			return false;
		}
		firstChildElement = firstChildElement.firstElementChild!;
	}
	return isSlideElement(firstChildElement);
};

const parseStylesFromElement = (element: Element, content: string): DomSlideStyle[] => {
	const styles: DomSlideStyle[] = [];

	const indexStart = 0;
	const indexEnd = content.length;

	if (hasOuterTag(element, "strong")) {
		styles.push({ content, indexStart, indexEnd, style: "bold" });
	}

	if (hasOuterTag(element, "i")) {
		styles.push({ content, indexStart, indexEnd, style: "italic" });
	}

	if (hasOuterTag(element, "u")) {
		styles.push({ content, indexStart, indexEnd, style: "underline" });
	}

	if (hasOuterTag(element, "mark")) {
		let parentElement = getOuterTag(element, "mark");
		let bgColor = parentElement?.className;
		if (bgColor !== undefined) {
			let colorAsHex = getTextBackgroundHexValue(bgColor);
			styles.push({ content, indexStart, indexEnd, style: "backgroundColor", colorAsHex });
		}
	}

	if (hasOuterTag(element, "span")) {
		let parentElement = getOuterTag(element, "span");
		let fontFamily = parentElement?.style.fontFamily;
		let correctedFontFamily = fontFamily?.split(",")[0].replace(/['"]/g, "") ?? "Arial";
		if (correctedFontFamily !== "") {
			styles.push({ content, indexStart, indexEnd, style: "fontFamily", fontFamily: correctedFontFamily });
		}
	}

	if (hasOuterTag(element, "li")) {
		let listType = checkListType(element);
		if (listType === "ol") {
			styles.push({ content, indexStart, indexEnd, style: "orderedList" });
		} else if (listType === "ul") {
			styles.push({ content, indexStart, indexEnd, style: "unorderedList" });
		}
	}

	if (elementIsInBlockQuote(element)) {
		styles.push({ content, indexStart, indexEnd, style: "blockQuote" });
	}

	let elementBlockTag = getBlockTag(element);
	if (elementBlockTag?.style.textAlign) {
		let alignment = elementBlockTag.style.textAlign;
		if (isValidAlignmentString(alignment)) {
			styles.push({ content, indexStart, indexEnd, style: "textAlignment", alignment });
		}
	} else if (!hasOuterTag(element, "li") && !elementIsInBlockQuote(element)) {
		styles.push({ content, indexStart, indexEnd, style: "textAlignment", alignment: "center" });
	}
	if (elementBlockTag && isBlockElement(elementBlockTag)) {
		let tagName = elementBlockTag.tagName.toLowerCase();
		styles.push({ content, indexStart, indexEnd, style: "blockTag", blockTag: tagName });
	}

	let fontSize = getFontSize(element);
	if (fontSize) {
		styles.push({ content, indexStart, indexEnd, style: "fontSize", fontSize });
	}

	const html = element.innerHTML;

	const strongRegEx = /<strong>(.*?)<\/strong>/g;
	const boldStyles = getInnerStyle(html, strongRegEx, "bold");
	styles.push(...boldStyles);

	const italicRegEx = /<i>(.*?)<\/i>/g;
	const italicStyles = getInnerStyle(html, italicRegEx, "italic");
	styles.push(...italicStyles);

	const underlineRegEx = /<u>(.*?)<\/u>/g;
	const underlineStyles = getInnerStyle(html, underlineRegEx, "underline");
	styles.push(...underlineStyles);

	return styles;
};

const getFontSize = (element: Element) => {
	let parentElement = element.parentElement;
	while (parentElement && !isBlockElement(parentElement) && parentElement.style.fontSize === "") {
		parentElement = parentElement.parentElement;
	}

	if (!parentElement || !parentElement.style.fontSize) return null;
	else {
		return parentElement.style.fontSize;
	}
};

const getBlockTag = (element: Element) => {
	let parentElement = element.parentElement;
	while (parentElement && !isBlockElement(parentElement)) {
		parentElement = parentElement.parentElement;
	}

	return parentElement;
};

const elementIsInBlockQuote = (element: Element) => {
	let parent = element.parentElement;
	while (parent && parent.tagName.toLowerCase() !== "div") {
		if (parent.tagName.toLowerCase() === "blockquote") {
			return true;
		}
		parent = parent.parentElement;
	}
	return false;
};

const checkListType = (element: Element) => {
	let parent = element.parentElement;

	while (parent != null && parent?.tagName.toLowerCase() !== "li" && !isBlockElement(parent)) {
		parent = parent.parentElement;
	}

	if (parent) {
		let listParent = parent.parentElement;
		let listParentType = listParent?.tagName.toLowerCase();
		return listParentType;
	}
};

const hasOuterTag = (element: Element, tagType: string) => {
	let parent = element.parentElement;
	while (parent != null && parent.tagName.toLowerCase() !== "div") {
		if (parent.tagName.toLowerCase() === tagType) {
			return true;
		}
		parent = parent.parentElement;
	}
	return false;
};

const getOuterTag = (element: Element, tagType: string) => {
	let parent = element.parentElement;
	while (parent != null && tagType !== parent.tagName.toLowerCase() && parent.tagName.toLowerCase() !== "div") {
		parent = parent.parentElement;
	}
	return parent;
};

const getInnerStyle = (html: string, regex: RegExp, style: SlideStyle): DomSlideStyle[] => {
	const styles: DomSlideStyle[] = [];

	let results: RegExpExecArray | null = null;

	while ((results = regex.exec(html)) !== null) {
		const contentElement = document.createElement("div");
		contentElement.innerHTML = results[0];
		const contentPlainText = contentElement.innerText;

		const substring = html.substring(0, regex.lastIndex);

		const substringElement = document.createElement("div");
		substringElement.innerHTML = substring;
		const substringPlainText = substringElement.innerText;

		const indexStart = substringPlainText.length - contentPlainText.length;
		const indexEnd = substringPlainText.length;
		styles.push({ indexStart, indexEnd, style, content: contentPlainText });
	}

	return styles;
};

const getElementInnerText = (element: Element): string => {
	const elementWithInnerText: any = element;
	if ("innerText" in elementWithInnerText) {
		return elementWithInnerText.innerText;
	}
	return element.textContent ?? "";
};

const getTextBackgroundHexValue = (className: string) => {
	if (className === "marker-purple") {
		return "#CE93D8";
	} else if (className === "marker-orange") {
		return "#FF9800";
	} else if (className === "marker-blue") {
		return "#72CDFD";
	} else if (className === "marker-light-red") {
		return "#FC6C63";
	} else if (className === "marker-green") {
		return "#63F963";
	} else if (className === "marker-yellow") {
		return "#FDFD77";
	} else if (className === "pen-red") {
		return "#e91313";
	} else if (className === "marker-black") {
		return "#000000";
	} else if (className === "pen-blue") {
		return "#72cdfd";
	}
};

const isLastSlideElementForBullet = (current: Element, next: Element) => {
	if (current == null || next == null) {
		return true;
	}

	let currentElementLiParent = current.parentElement;
	while (currentElementLiParent != null && !isListElement(currentElementLiParent) && !isBlockElement(currentElementLiParent)) {
		currentElementLiParent = currentElementLiParent.parentElement;
	}

	let nextElementLiParent = next.parentElement;
	while (nextElementLiParent != null && !isListElement(nextElementLiParent) && !isBlockElement(nextElementLiParent)) {
		nextElementLiParent = nextElementLiParent.parentElement;
	}

	if (
		currentElementLiParent != null &&
		isListElement(currentElementLiParent) &&
		nextElementLiParent != null &&
		isListElement(nextElementLiParent)
	) {
		return currentElementLiParent !== nextElementLiParent;
	}

	return true;
};

export const EditorHelper = {
	getEditorDomHtml: () => {
		const editorElement = document.querySelector("#editor");
		return editorElement?.innerHTML ?? "";
	},
	getSlideElements: (editorHtml = document) => {
		return editorHtml.getElementsByTagName("slide");
	},
	getEditorSlides: (editorHtml: string = "") => {
		let slideElements = null;
		if (editorHtml) {
			let doc = new DOMParser().parseFromString(editorHtml, "text/html");
			slideElements = [...EditorHelper.getSlideElements(doc)];
		} else {
			slideElements = [...EditorHelper.getSlideElements()];
		}

		const editorSlides: EditorSlide[] = [];
		let count = 0;
		let bulletCount = 0;
		let domSlides: DomSlide[] = [];

		slideElements.forEach((slideElement, index, slideElementArray) => {
			let isOrderedList = false;
			let isUnorderedList = false;
			let isBlockQuote = false;
			let textAlignment = undefined;
			let fontSize = undefined;
			let incBulletCount = false;

			let id = slideElement.id;
			if (id === "") {
				id = generateGuid();
				slideElement.setAttribute("id", id);
			}

			let slideContent = getElementInnerText(slideElement);
			if (slideElement.nextElementSibling !== null && slideElement.nextElementSibling.tagName.toLowerCase() === "br") {
				slideContent += "\n";
			}

			let slideStyles = parseStylesFromElement(slideElement, slideContent);

			if (slideStyles.some((style) => style.style === "orderedList")) {
				isOrderedList = true;
			} else if (slideStyles.some((style) => style.style === "unorderedList")) {
				isUnorderedList = true;
			}

			if (slideStyles.some((style) => style.style === "blockQuote")) {
				isBlockQuote = true;
			}
			if (slideStyles.some((style) => style.style === "textAlignment")) {
				textAlignment = slideStyles.filter((style) => style.style === "textAlignment")[0].alignment;
			}
			if (slideStyles.some((style) => style.style === "fontSize")) {
				fontSize = slideStyles.filter((style) => style.style === "fontSize")[0].fontSize;
			}

			if (isOrderedList || isUnorderedList) {
				let isLastContentForCurrentBullet = isLastSlideElementForBullet(slideElement, slideElementArray[index + 1]);
				if (isLastContentForCurrentBullet) {
					incBulletCount = true;
				}
			}

			let domSlide: DomSlide = {
				id,
				html: slideElement.innerHTML,
				styles: slideStyles,
				content: slideContent,
				isOrderedList,
				isUnorderedList,
				isBlockQuote,
				textAlignment,
				fontSize,
				bulletNumber: isOrderedList || isUnorderedList ? bulletCount : undefined,
			};
			domSlides.push(domSlide);

			if (incBulletCount) {
				bulletCount++;
				incBulletCount = false;
			}

			// This slide is next to another slide on the DOM that we want to treat as the same slide
			if (!isLastSlide(slideElement)) {
				if (slideElement.hasAttribute("count")) {
					slideElement.removeAttribute("count");
				}
				return;
			}

			count++;
			const countStr = count.toString();
			if (slideElement.getAttribute("count") !== countStr) {
				slideElement.setAttribute("count", countStr);
			}

			editorSlides.push({ domSlides });
			domSlides = [];
		});

		return editorSlides;
	},
	getPreviewSlides: (editorSlides: EditorSlide[]) => {
		const MAX_LINE_LENGTH = 80;
		let previewSlides: PreviewSlide[] = [];

		editorSlides.forEach((editorSlide) => {
			const domSlideIds = editorSlide.domSlides.map((domSlide) => domSlide.id);
			const slideLines = getContentSeperatedByLine(editorSlide, MAX_LINE_LENGTH);
			const slideLinesSplitIntoChars = getSlideLineChars(slideLines, editorSlide);

			let previewSlide: PreviewSlide = {
				domSlideIds,
				lines: slideLinesSplitIntoChars,
			};
			previewSlides.push(previewSlide);
		});

		return previewSlides;
	},
	getFormatBubblePostitionSettings: (selections: CkEditorSelection[], mobile: boolean, iOS: boolean) => {
		let hasSelection = selections.length > 0;
		if (!hasSelection) {
			return undefined;
		}

		let bubbleWidth = 95;
		let message = "";
		let action = SlideAction.Add;

		let hasSlideInSelection = selections.some((s) => s.attributes != null && s.attributes.addSlide != null);
		let allSelectedIsSlide = selections.every((s) => s.attributes != null && s.attributes.addSlide != null);
		let firstElement = selections[0];
		let firstElementIsSlide = firstElement.attributes != null && firstElement.attributes.addSlide != null;
		let lastElement = selections[selections.length - 1];
		let lastElementIsSlide = lastElement.attributes != null && lastElement.attributes.addSlide != null;

		let isAddSlide = !hasSlideInSelection;
		let isExtendSlide = hasSelection && !allSelectedIsSlide;
		let isRemoveSlide = allSelectedIsSlide;

		let rightToLeftExtension = isExtendSlide && firstElementIsSlide && !lastElementIsSlide;
		let middleExtension = isExtendSlide && firstElementIsSlide && lastElementIsSlide;

		if (isAddSlide) {
			bubbleWidth = 95;
			message = "Add Slide";
			action = SlideAction.Add;
		} else if (isExtendSlide) {
			bubbleWidth = 120;
			message = "Extend Slide";
			if (rightToLeftExtension) {
				action = SlideAction.RightToLeftExtend;
			} else if (middleExtension) {
				action = SlideAction.MiddleExtend;
			} else {
				action = SlideAction.LeftToRightExtend;
			}
		} else if (isRemoveSlide) {
			bubbleWidth = 130;
			message = "Remove Slide";
			action = SlideAction.Remove;
		}

		let selectionLocation = window.getSelection()!.getRangeAt(0).getBoundingClientRect();
		if (mobile) {
			let mobile: MobileFormatBubbleSettings = {
				right: 5 + (iOS ? 0 : 50),
				top: selectionLocation.top - 2,
				action,
			};
			return mobile;
		} else {
			let desktop: DesktopFormatBubbleSettings = {
				left: (selectionLocation.left + selectionLocation.left + selectionLocation.width - bubbleWidth) / 2,
				top: selectionLocation.top > 150 ? selectionLocation.top - 50 : selectionLocation.top + 30,
				width: bubbleWidth,
				message: message,
				action,
				isBolded: CkEditorHelper.isAttributeActionActive(selections, "bold"),
				isItalic: CkEditorHelper.isAttributeActionActive(selections, "italic"),
				isUnderlined: CkEditorHelper.isAttributeActionActive(selections, "underline"),
				invertArrowDirection: selectionLocation.top < 150,
			};
			return desktop;
		}
	},
	checkIfClipBoardContainsText: async () => {
		let copiedText = await (window.navigator as any).clipboard.readText();
		if (copiedText && copiedText.length > 0) {
			return copiedText;
		} else {
			return null;
		}
	},
	executeSlideAction: (slideAction: SlideAction, editor: Editor) => {
		if (slideAction === SlideAction.MiddleExtend) {
			return onMiddleSlideExtend(editor);
		} else if (slideAction === SlideAction.RightToLeftExtend) {
			return onRightToLeftSlideExtend(editor);
		} else {
			return onToggleSlide(slideAction, editor);
		}
	},
	getTextFromSelections: (selections: CkEditorSelection[]) => {
		let data: string[] = [];
		selections.forEach((selection: CkEditorSelection) => {
			visit(selection, gatherSentences);

			function gatherSentences(selection: CkEditorSelection) {
				if (selection.data) {
					data.push(selection.data);
				}
			}
		});
		return data.join(" ");
	},
};

const getContentSeperatedByLine = (editorSlide: EditorSlide, maxLen: number) => {
	const MAX_LINE_LENGTH = maxLen;
	let lines: string[] = [];
	let lineNum = 0;
	editorSlide.domSlides.forEach((domSlide, index, domSlideArray) => {
		let { content, isBlockQuote, isOrderedList, isUnorderedList } = domSlide;
		let contentLength = content.length;

		if (lines[lineNum] === undefined) {
			lines[lineNum] = "";
		}

		if (isOrderedList || isUnorderedList) {
			if (domSlideArray[index + 1] === undefined) {
				if (domSlideArray[index - 1] !== undefined && domSlide.bulletNumber === domSlideArray[index - 1].bulletNumber) {
					lines[lineNum] += content;
				} else {
					lines[lineNum + 1] = content;
					lineNum += 2;
				}
				return;
			}

			if (domSlide.bulletNumber === domSlideArray[index + 1].bulletNumber) {
				if (domSlideArray[index - 1] !== undefined && domSlide.bulletNumber !== domSlideArray[index - 1].bulletNumber) {
					lineNum++;
					lines[lineNum] = "";
				}
				lines[lineNum] += content;
			} else if (domSlideArray[index - 1] !== undefined && domSlide.bulletNumber === domSlideArray[index - 1].bulletNumber) {
				lines[lineNum] += content;
				lineNum++;
			} else {
				if (lines[lineNum] !== "") {
					lineNum++;
					lines[lineNum] = "";
				}
				lines[lineNum] = content;
			}
			return;
		}

		if (isBlockQuote) {
			if (lines[lineNum].length > 0) {
				lines[lineNum + 1] = content;
				lineNum += 2;
			} else {
				lines[lineNum] = content;
				lineNum++;
			}
			lines[lineNum] = "";
			return;
		}

		if (contentLength > MAX_LINE_LENGTH || contentLength + lines[lineNum].length > MAX_LINE_LENGTH) {
			let words = content.split(" ");
			words.forEach((word) => {
				if (word.length + lines[lineNum].length < MAX_LINE_LENGTH) {
					lines[lineNum] += word + " ";
				} else {
					lineNum++;
					lines[lineNum] = word + " ";
				}
			});
		} else {
			lines[lineNum] += content;
		}
	});

	if (lines[lines.length - 1] === "") {
		lines.splice(lines.length - 1, 1);
	}

	return lines;
};

const getSlideLineChars = (contentLines: string[], editorSlide: EditorSlide) => {
	let indexCorrectedLinesSplitIntoChars = getIndexCorrectedLineChars(contentLines);
	let indexCorrectedDomSlideStyles = getIndexCorrectedStyles(editorSlide.domSlides);

	indexCorrectedDomSlideStyles.forEach((style) => {
		let styleIndexStart = style.indexStart;
		let styleIndexEnd = style.indexEnd;

		indexCorrectedLinesSplitIntoChars.forEach((line) => {
			const lastIndex = line.lineChars.length - 1;
			let lastChar = line.lineChars[lastIndex];
			let firstChar = line.lineChars[0];
			if (lastChar === undefined || firstChar === undefined) {
				return;
			}

			let lineIndexEnd = lastChar.indexInContent;
			let lineIndexStart = firstChar.indexInContent;

			if (style.style === "orderedList") {
				if (lineIndexStart === styleIndexStart && lineIndexEnd === styleIndexEnd) {
					line.isOrderedList = true;
				}
			} else if (style.style === "unorderedList") {
				if (lineIndexStart === styleIndexStart && lineIndexEnd === styleIndexEnd) {
					line.isUnorderedList = true;
				}
			}

			if (style.style === "blockQuote") {
				if (lineIndexStart === styleIndexStart && lineIndexEnd === styleIndexEnd) {
					line.isBlockQuote = true;
				}
			}

			if (style.style === "textAlignment" && isValidAlignmentString(style.alignment)) {
				line.textAlignment = style.alignment;
			}

			if (style.style === "blockTag" && style.blockTag !== undefined) {
				line.blockTag = style.blockTag;
			}

			line.lineChars.forEach((char) => {
				if (char.indexInContent !== undefined && char.indexInContent <= styleIndexEnd && char.indexInContent >= styleIndexStart) {
					if (style.style === "bold") {
						char.isBold = true;
					} else if (style.style === "italic") {
						char.isItalics = true;
					} else if (style.style === "underline") {
						char.isUnderlined = true;
					} else if (style.style === "backgroundColor") {
						char.backgroundColor = style.colorAsHex;
					} else if (style.style === "fontFamily" && style.fontFamily !== undefined) {
						char.fontFamily = style.fontFamily;
					} else if (style.style === "fontSize" && style.fontSize !== undefined) {
						char.fontSize = style.fontSize;
					}
				}
			});
		});
	});

	return indexCorrectedLinesSplitIntoChars;
};

const getIndexCorrectedLineChars = (contentLines: string[]) => {
	let trueIndex = 0;
	let correctedChars = contentLines.map((line) => {
		let chars = line.split("");
		let indexCorrectedChars = chars.map((char) => {
			let indexCorrectedChar: SlideLineChar = {
				char,
				indexInContent: trueIndex,
				isBold: false,
				isItalics: false,
				isUnderlined: false,
				fontFamily: "Arial",
			};
			trueIndex++;

			return indexCorrectedChar;
		});

		let slideLine: SlideLine = {
			lineChars: indexCorrectedChars,
			indentLeft: false,
			indentRight: false,
			isBlockQuote: false,
			isOrderedList: false,
			isUnorderedList: false,
			textAlignment: undefined,
		};

		return slideLine;
	});

	return correctedChars;
};

const getIndexCorrectedStyles = (slides: DomSlide[]) => {
	let bulletStartIndex: number | undefined = undefined;
	let bulletContentAccumulator = "";
	let bulletContent = "";
	let domSlideWithCombinedIndexCorrectedStyles = slides.reduce(
		(accumulator, currentSlide, index, slideArray) => {
			if (currentSlide.bulletNumber !== undefined) {
				let next = slideArray[index + 1];
				let prev = slideArray[index - 1];
				if (next !== undefined && next.bulletNumber === currentSlide.bulletNumber) {
					bulletContent += currentSlide.content;
					if (bulletStartIndex === undefined) {
						bulletStartIndex = bulletContentAccumulator.length === 0 ? 0 : bulletContentAccumulator.length;
					}
					bulletContentAccumulator += currentSlide.content;
				} else if (
					prev !== undefined &&
					prev.bulletNumber === currentSlide.bulletNumber &&
					(next === undefined || next.bulletNumber !== currentSlide.bulletNumber)
				) {
					bulletContentAccumulator += currentSlide.content;
					bulletContent += currentSlide.content;

					let listStyle = currentSlide.styles.filter((style) => style.style === "unorderedList" || style.style === "orderedList")[0];
					listStyle.content = bulletContent;
					listStyle.indexEnd = bulletContentAccumulator.length - 1;
					listStyle.indexStart = bulletStartIndex !== undefined ? bulletStartIndex : listStyle.indexStart;
					accumulator.styles = accumulator.styles.concat(listStyle);

					bulletContent = "";
					bulletStartIndex = undefined;
				} else {
					let listStyle = currentSlide.styles.filter((style) => style.style === "unorderedList" || style.style === "orderedList")[0];
					listStyle.indexStart = bulletContentAccumulator.length;
					listStyle.indexEnd = listStyle.indexStart + currentSlide.content.length - 1;

					accumulator.styles = accumulator.styles.concat(listStyle);

					bulletContentAccumulator += currentSlide.content;
					bulletContent = "";
				}
			}

			let stylesWithoutListStyles = currentSlide.styles.filter((style) => style.style !== "unorderedList" && style.style !== "orderedList");
			let correctedIndexStyles = stylesWithoutListStyles.map((style) => {
				style.indexStart += accumulator.content.length ?? 0;
				style.indexEnd += accumulator.content.length - 1 ?? 0;
				return style;
			});
			accumulator.styles = accumulator.styles.concat(correctedIndexStyles);
			accumulator.content = accumulator.content + currentSlide.content;
			return accumulator;
		},
		{ id: "", html: "", content: "", styles: [] }
	);

	return domSlideWithCombinedIndexCorrectedStyles.styles;
};

const onToggleSlide = (action: SlideAction, editor: Editor) => {
	editor.execute("addSlide");
	let editorData = editor.getData();
	let slides = CkEditorHelper.getSlides(editorData);
	let slideCount = slides.length;
	if (action === SlideAction.Add) {
		EventTracking.Builder.onAddSlide(slideCount);
	} else if (action === SlideAction.LeftToRightExtend) {
		EventTracking.Builder.onExtendSlide(slideCount);
	} else {
		EventTracking.Builder.onRemoveSlide(slideCount);
	}

	return slides;
};

const onRightToLeftSlideExtend = (editor: Editor) => {
	editor.execute("addSlide");
	editor.execute("addSlide");
	editor.editing.view.focus();

	let editorData = editor.getData();
	let slides = CkEditorHelper.getSlides(editorData);
	let slideCount = slides.length;
	EventTracking.Builder.onExtendSlide(slideCount);

	return slides;
};

const onMiddleSlideExtend = (editor: Editor) => {
	editor.execute("addSlide");
	editor.execute("addSlide");

	let editorData = editor.getData();
	let slides = CkEditorHelper.getSlides(editorData);
	let slideCount = slides.length;
	EventTracking.Builder.onExtendSlide(slideCount);
	return slides;
};
